import React, { useMemo } from "react";
import VaultHeader from "./VaultHeader";
import VaultGrid from "./VaultGrid";
import VaultDisclaimer from "./VaultDisclaimer";
import { createCoreVaultConfig } from "./constants";
import { useVaultSearch } from "./hooks";
import { VaultItem } from "./types";
import NoSearchResults from "./NoSearchResults";
import UTMLink from "@components/UTMLink";
import BaseImage from "@components/BaseImage";
import { trackEvent } from "@lib/utils";
import { BANNER_CLICKED } from "@constants/analytics";
import { useVaultTvlApy } from "@hooks/useVaultTvlApy";

const VaultLanding: React.FC = () => {
  // Fetch all vault data directly - single API call with total TVL included
  const {
    agETH,
    hgETH,
    sbUSD,
    isLoadingApy,
    isLoadingTvl,
    formattedTotalTvl,
    totalTvl,
  } = useVaultTvlApy("all");

  // Create vault configurations
  const allVaults: VaultItem[] = useMemo(() => {
    const coreVaults = createCoreVaultConfig(
      hgETH,
      agETH,
      sbUSD,
      isLoadingApy,
      isLoadingTvl
    );
    // const partnerVaults = createPartnerVaultConfig(
    //   tacRsETH,
    //   isLoadingApy,
    //   isLoadingTvl
    // );
    return [...coreVaults];
  }, [hgETH, agETH, sbUSD, isLoadingApy, isLoadingTvl]);

  const { searchQuery, setSearchQuery, coreVaults, partnerVaults } =
    useVaultSearch(allVaults);

  return (
    <div className="flex flex-col gap-8 justify-center items-center w-full mt-4 md:-mt-6">
      <div className="flex flex-col gap-8 justify-between items-center w-full max-w-sm sm:max-w-lg md:max-w-4xl lg:max-w-6xl xl:max-w-6xl">
        {/* Banner - Responsive images for different screen sizes */}
        <div className="w-full">
          <UTMLink
            href="/gain/growth-vault/"
            onClick={() =>
              trackEvent(BANNER_CLICKED, {
                location: "vault_landing",
                redirect_url: "/gain/growth-vault/",
              })
            }
          >
            {/* Mobile banner - visible only on mobile */}
            <BaseImage
              src={"/assets/insured_mweb.avif"}
              alt="restake banner"
              width={768}
              height={200}
              className="!w-full !h-auto !object-contain block md:hidden"
              style={{
                borderRadius: "24px",
              }}
            />
            {/* Tablet banner - visible only on tablet */}
            <BaseImage
              src={"/assets/insured_tab.avif"}
              alt="restake banner"
              width={1024}
              height={200}
              className="!w-full !h-auto !object-contain hidden md:block lg:hidden"
              style={{
                borderRadius: "24px",
              }}
            />
            {/* Desktop banner - visible only on desktop */}
            <BaseImage
              src={"/assets/insured.avif"}
              alt="restake banner"
              width={1200}
              height={200}
              className="!w-full !h-auto !object-contain hidden lg:block"
              style={{
                borderRadius: "24px",
              }}
            />
          </UTMLink>
        </div>

        {/* Header */}
        <VaultHeader
          totalTvlData={{
            totalTvl,
            formattedTotalTvl,
            totalTvlLoading: isLoadingTvl,
          }}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />

        {/* Core Vaults Section */}
        <VaultGrid title="Core vaults" vaults={coreVaults} />

        {/* Partner Vaults Section */}
        <VaultGrid title="Partner vaults" vaults={partnerVaults} />

        {coreVaults.length === 0 &&
          partnerVaults.length === 0 &&
          searchQuery.trim() !== "" && (
            <NoSearchResults onSeeAll={() => setSearchQuery("")} />
          )}

        {/* Disclaimer */}
        <VaultDisclaimer />
      </div>
    </div>
  );
};

export default VaultLanding;
